import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Award, Brain, Globe, Shield, ArrowRight, Calendar, Clock, MessageSquare, Mail, CheckCircle } from 'lucide-react';

// Declare global recaptcha type
declare global {
  interface Window {
    grecaptcha: any;
    onRecaptchaLoad: () => void;
  }
}

const Leadership = () => {
  const [selectedMeetingType, setSelectedMeetingType] = useState<string | null>(null);
  const [minDate, setMinDate] = useState<string>('');
  const [formData, setFormData] = useState({
    meetingType: '',
    date: '',
    timeSlot: '',
  });
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [showRecaptchaError, setShowRecaptchaError] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const availableTimeSlots = [
    "9:00 AM - 10:00 AM",
    "10:00 AM - 11:00 AM",
    "11:00 AM - 12:00 PM",
    "2:00 PM - 3:00 PM",
    "3:00 PM - 4:00 PM",
    "4:00 PM - 5:00 PM"
  ];

  const meetingTypes = [
    {
      title: "Security Strategy Discussion",
      duration: "60 min",
      description: "Strategic discussion about your organization's security needs and objectives"
    },
    {
      title: "Technical Consultation",
      duration: "45 min",
      description: "Deep dive into technical security requirements and solutions"
    },
    {
      title: "Executive Briefing",
      duration: "30 min",
      description: "High-level overview of security solutions and capabilities"
    }
  ];

  const leaders = [
    {
      name: "Dr. Ghassan Abukar, Ph.D.",
      title: "Chief Information Officer",
      image: "/assets/ghassan-abukar.jpg?v=2",
      description: "Dr. Ghassan Abukar is an accomplished Chief Information Officer (CIO) and IT Director with over 18 years of progressive leadership experience spanning aviation, manufacturing, higher education, and consulting industries. Possesses a Ph.D. in Management with a specialization in Designing Sustainable Systems, complemented by dual Master's degrees in Systems Engineering and Information Engineering and Management. Proven track record in driving significant technology transformations, aligning IT strategy with business objectives, and delivering measurable outcomes in cybersecurity, operational efficiency, and digital innovation.",
      expertise: [],
      achievements: []
    },
    {
      name: "Dr. George Awad",
      title: "Chief Operating Officer",
      image: "/assets/george-awad.jpg",
      description: "Dr. George Awad serves as AstronShield's Chief Operating Officer, bringing over two decades of experience in operational excellence and strategic leadership. His expertise in scaling cybersecurity operations and optimizing service delivery has been instrumental in AstronShield's growth and success in serving enterprise clients globally.",
      expertise: [],
      achievements: []
    }
  ];

  const values = [
    {
      icon: Shield,
      title: "Security Excellence",
      description: "Committed to delivering the highest standards of cybersecurity protection"
    },
    {
      icon: Brain,
      title: "Innovation",
      description: "Driving technological advancement in cybersecurity solutions"
    },
    {
      icon: Globe,
      title: "Global Impact",
      description: "Making a difference in cybersecurity on a global scale"
    },
    {
      icon: Award,
      title: "Leadership",
      description: "Setting the standard for excellence in cybersecurity leadership"
    }
  ];

  // Set minimum date to today when component mounts
  useEffect(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    setMinDate(`${year}-${month}-${day}`);
    
    // Load reCAPTCHA script
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit';
    script.async = true;
    script.defer = true;
    
    window.onRecaptchaLoad = () => {
      setRecaptchaLoaded(true);
    };
    
    document.body.appendChild(script);
    
    return () => {
      document.body.removeChild(script);
      (window as any).onRecaptchaLoad = undefined;
    };
  }, []);
  
  // Initialize reCAPTCHA when it's loaded
  useEffect(() => {
    if (recaptchaLoaded && window.grecaptcha) {
      try {
        window.grecaptcha.render('recaptcha-container', {
          sitekey: '6LeWfxorAAAAAHuXucPcVo0Rd0nCrIkXjezFDR8e'
        });
      } catch (error) {
        console.error('Error rendering reCAPTCHA:', error);
      }
    }
  }, [recaptchaLoaded]);

  // Update form data when inputs change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle meeting type selection
  const handleMeetingTypeSelect = (meetingType: string) => {
    setSelectedMeetingType(meetingType);
    setFormData(prev => ({
      ...prev,
      meetingType
    }));
  };
  
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset error state
    setShowRecaptchaError(false);
    
    // Verify reCAPTCHA
    if (window.grecaptcha) {
      const recaptchaResponse = window.grecaptcha.getResponse();
      if (!recaptchaResponse) {
        setShowRecaptchaError(true);
        return;
      }
      
      // If reCAPTCHA verification passed, submit the form
      const form = e.target as HTMLFormElement;
      
      // Create a FormData object to get the form data
      const formDataObj = new FormData(form);
      
      // Submit the form using fetch
      fetch(form.action, {
        method: form.method,
        body: formDataObj,
        headers: {
          Accept: 'application/json'
        }
      })
        .then(response => {
          if (response.ok) {
            // Show thank you message
            setFormSubmitted(true);
            // Reset form data
            setSelectedMeetingType(null);
            setFormData({
              meetingType: '',
              date: '',
              timeSlot: '',
            });
          } else {
            console.error('Form submission failed');
            alert('There was a problem submitting your form. Please try again.');
          }
        })
        .catch(error => {
          console.error('Form submission error:', error);
          alert('There was a problem submitting your form. Please try again.');
        });
    } else {
      console.error('reCAPTCHA not loaded');
      setShowRecaptchaError(true);
    }
  };

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-6"
          >
            <Users className="h-16 w-16 text-primary animate-glow" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="text-gradient">Leadership Team</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted max-w-3xl mx-auto"
          >
            Meet the visionaries leading AstronShield's mission to revolutionize cybersecurity
          </motion.p>
        </div>

        {/* Leadership Profiles */}
        <div className="mb-20 space-y-6">
          {leaders.map((leader, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.2 }}
              className="glass-panel rounded-xl p-6 hover-card"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
                <div className="flex flex-col items-center md:items-start">
                  <div className="w-48 h-48 rounded-xl overflow-hidden mb-4">
                    <img 
                      src={leader.image} 
                      alt={leader.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h2 className="text-xl font-bold mb-1">{leader.name}</h2>
                  <p className="text-primary font-semibold">{leader.title}</p>
                </div>
                
                <div className="md:col-span-2">
                  <p className="text-muted leading-relaxed">
                    {leader.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Leadership Values */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Leadership Values</h2>
            <p className="text-lg text-muted">
              The principles that guide our leadership and decision-making
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="glass-panel rounded-xl p-6 hover-card"
              >
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg mb-6">
                  <value.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{value.title}</h3>
                <p className="text-muted">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6">Connect With Our Leadership</h2>
            <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
              Schedule a meeting with our leadership team to discuss your organization's security needs
            </p>
            
            {/* Scheduling Form */}
            <div className="max-w-2xl mx-auto">
              {formSubmitted ? (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center p-8 glass-panel rounded-xl border-2 border-primary"
                >
                  <CheckCircle className="h-16 w-16 text-primary mx-auto mb-6" />
                  <h3 className="text-2xl font-bold mb-4">Thank You!</h3>
                  <p className="text-lg text-muted mb-6">
                    Your meeting request has been successfully submitted. One of our leadership team members will be in touch with you shortly to confirm your appointment.
                  </p>
                  <button
                    onClick={() => setFormSubmitted(false)}
                    className="btn-primary px-8"
                  >
                    <span className="flex items-center justify-center">
                      Schedule Another Meeting
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </span>
                  </button>
                </motion.div>
              ) : (
                <form action="https://formspree.io/f/mvgkyjwy" method="POST" className="space-y-6" onSubmit={handleSubmit}>
                  <input type="hidden" name="_subject" value={`Leadership Meeting Request: ${selectedMeetingType || 'No Meeting Type Selected'}`} />
                  
                  <input
                    type="hidden"
                    name="summary"
                    value={`Meeting Type: ${formData.meetingType}${formData.date ? `, Date: ${formData.date}` : ''}${formData.timeSlot ? `, Time: ${formData.timeSlot}` : ''}`}
                  />

                  <label className="block text-sm font-medium mb-4">Select Meeting Type</label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {meetingTypes.map((type, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleMeetingTypeSelect(type.title)}
                        className={`glass-panel rounded-xl p-6 hover:border-primary/50 cursor-pointer transition-all text-left ${
                          selectedMeetingType === type.title 
                            ? 'border-2 border-primary bg-primary/10 transform scale-[1.02]' 
                            : 'border border-white/10'
                        }`}
                      >
                        <Calendar className="h-6 w-6 text-primary mb-4" />
                        <h3 className="text-lg font-semibold mb-2">{type.title}</h3>
                        <p className="text-sm text-muted mb-2">{type.description}</p>
                        <span className="flex items-center text-sm text-primary">
                          <Clock className="h-4 w-4 mr-1" />
                          {type.duration}
                        </span>
                      </button>
                    ))}
                  </div>
                  <input 
                    type="hidden" 
                    name="meetingType" 
                    value={selectedMeetingType || ''}
                  />

                  {/* Date Selection */}
                  <div className="glass-panel rounded-xl p-6">
                    <h3 className="text-lg font-semibold mb-4">Select Date & Time</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium mb-2">Date</label>
                        <input
                          type="date"
                          name="date"
                          min={minDate}
                          value={formData.date}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-primary/50 focus:ring-1 focus:ring-primary/50"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Time Slot</label>
                        <select
                          name="timeSlot"
                          value={formData.timeSlot}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-primary/50 focus:ring-1 focus:ring-primary/50"
                          required
                        >
                          <option value="">Select a time</option>
                          {availableTimeSlots.map((slot, index) => (
                            <option key={index} value={slot}>{slot}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="glass-panel rounded-xl p-6">
                    <h3 className="text-lg font-semibold mb-4">Your Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium mb-2">Name</label>
                        <input
                          type="text"
                          name="name"
                          className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-primary/50 focus:ring-1 focus:ring-primary/50"
                          placeholder="Your name"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Email</label>
                        <input
                          type="email"
                          name="email"
                          className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-primary/50 focus:ring-1 focus:ring-primary/50"
                          placeholder="your@email.com"
                          required
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium mb-2">Company</label>
                        <input
                          type="text"
                          name="company"
                          className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-primary/50 focus:ring-1 focus:ring-primary/50"
                          placeholder="Your company name"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium mb-2">Meeting Agenda</label>
                        <textarea
                          name="agenda"
                          className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-primary/50 focus:ring-1 focus:ring-primary/50"
                          rows={4}
                          placeholder="Brief description of what you'd like to discuss"
                        />
                      </div>
                    </div>
                  </div>

                  {/* reCAPTCHA */}
                  <div className="flex flex-col items-center">
                    <div id="recaptcha-container"></div>
                    {showRecaptchaError && (
                      <p className="text-red-500 mt-2">Please complete the reCAPTCHA before submitting.</p>
                    )}
                  </div>

                  <button type="submit" className="w-full btn-primary">
                    <span className="flex items-center justify-center">
                      Schedule Meeting
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </span>
                  </button>
                </form>
              )}

              {/* Alternative Contact Methods */}
              <div className="mt-8 pt-8 border-t border-white/10">
                <h3 className="text-lg font-semibold mb-4 text-center">Other Ways to Connect</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <a href="mailto:contact@astronshield.com" className="flex items-center justify-center glass-panel rounded-xl p-4 hover:border-primary/50 transition-colors">
                    <Mail className="h-5 w-5 text-primary mr-2" />
                    <span>Email Our Team</span>
                  </a>
                  <a href="tel:+18889917050" className="flex items-center justify-center glass-panel rounded-xl p-4 hover:border-primary/50 transition-colors">
                    <MessageSquare className="h-5 w-5 text-primary mr-2" />
                    <span>Request Call Back</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Leadership;